<template>
  <div>
    <!-- Mobile Sidebar Backdrop -->
    <div
      v-if="isOffcanvasActive"
      class="sidebar-backdrop"
      @click="closeOffcanvas"
    ></div>

    <nav class="navbar p-0 fixed-top d-flex align-items-center">
      <div class="navbar-menu-wrapper w-100 d-flex align-items-center justify-content-between px-3">
        <!-- Left Section -->
        <div class="d-flex align-items-center">
          <!-- Desktop Sidebar Toggler -->
          <button
            class="navbar-toggler align-self-center d-none d-lg-block mr-2 kh-hud-btn"
            type="button"
            @click="toggleSidebarMinimize"
            :title="$t('nav.toggleSidebar')"
            :aria-label="$t('nav.toggleSidebar')"
          >
            <span class="mdi mdi-menu text-white" style="font-size: 20px;" aria-hidden="true"></span>
          </button>

          <!-- Mobile Hamburger Toggler -->
          <button
            class="navbar-toggler d-lg-none mr-2 p-1 kh-hud-btn"
            type="button"
            @click="toggleOffcanvas"
            :title="$t('nav.openMenu')"
            :aria-label="$t('nav.openMenu')"
          >
            <span class="mdi mdi-menu text-white" style="font-size: 22px;" aria-hidden="true"></span>
          </button>

          <!-- Mobile Brand Logo -->
          <router-link to="/dashboard" class="d-lg-none d-flex align-items-center gap-2 mr-2 text-decoration-none">
            <div class="kh-pennant" style="width: 26px; height: 26px; font-size: 13px;">
              <i class="mdi mdi-shield-crown-outline" style="color: #fbbf24;" aria-hidden="true"></i>
            </div>
            <span class="kh-title text-white" style="font-size: 14px;">GAMITEAM</span>
          </router-link>

          <!-- Desktop Search / Command Palette Trigger -->
          <div class="d-none d-md-flex align-items-center ml-2">
            <button
              type="button"
              class="kh-cmd-trigger d-flex align-items-center py-1 px-3"
              @click="showCmd = true"
            >
              <span class="mdi mdi-magnify mr-2" style="color: #7dd3fc;" aria-hidden="true"></span>
              <span class="small mr-3" style="color: #bae6fd; font-weight: 700;">
                {{ $t('common.search') }}
              </span>
              <kbd class="kh-cmd-kbd">⌘K</kbd>
            </button>
          </div>
        </div>

        <!-- Center (Mobile Search Icon) -->
        <div class="d-md-none">
          <button
            type="button"
            class="btn btn-outline-info btn-sm p-1 px-2"
            @click="showCmd = true"
            :title="$t('common.search')"
            :aria-label="$t('common.search')"
          >
            <i class="mdi mdi-magnify" aria-hidden="true"></i>
          </button>
        </div>

        <!-- Right Controls Section -->
        <div class="d-flex align-items-center gap-2">
          <!-- Realm Status Badge (Desktop) -->
          <div class="d-none d-xl-flex align-items-center kh-realm-pill px-2 py-1 mr-1">
            <i class="mdi mdi-shield-check-outline mr-1" style="color: #fbbf24; font-size: 13px;" aria-hidden="true"></i>
            <span class="kh-title-gold" style="font-size: 10px;">
              {{ isSuperAdmin ? $t('nav.realmSovereign') : (isMaker ? $t('nav.realmGuildMaster') : $t('nav.realmRoyalCommand')) }}
            </span>
          </div>

          <!-- Language Switcher Pill -->
          <div class="d-flex align-items-center kh-lang-switch px-1 py-1">
            <button
              type="button"
              class="kh-lang-opt"
              :class="{ active: $currentLocale === 'en' }"
              @click="$setLocale('en')"
            >
              EN
            </button>
            <button
              type="button"
              class="kh-lang-opt"
              :class="{ active: $currentLocale === 'id' }"
              @click="$setLocale('id')"
            >
              ID
            </button>
          </div>

          <!-- User Profile Dropdown -->
          <div class="dropdown">
            <button
              class="kh-user-pill d-flex align-items-center p-1 pr-2"
              type="button"
              id="userMenuBtn"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <div class="kh-user-crest mr-1">
                {{ (userName || 'U')[0].toUpperCase() }}
              </div>
              <span class="kh-title text-white d-none d-sm-inline mx-1" style="font-size: 11px;">
                {{ userName }}
              </span>
              <i class="mdi mdi-chevron-down small mr-1" style="color: #fde047;" aria-hidden="true"></i>
            </button>

            <ul class="dropdown-menu dropdown-menu-end shadow-lg py-2 kh-dropdown-menu" aria-labelledby="userMenuBtn" style="min-width: 230px; z-index: 1055;">
              <li class="px-3 py-2 mb-1" style="border-bottom: 1.5px solid rgba(56, 189, 248, 0.25);">
                <div class="kh-title text-white text-truncate" style="font-size: 12px;">{{ userName }}</div>
                <div class="small text-truncate" style="color: #93c5fd;">{{ userEmail }}</div>
                <span class="badge badge-warning text-dark font-weight-bold mt-1">
                  <i class="mdi mdi-shield-crown-outline mr-1" aria-hidden="true"></i>{{ roleBadgeLabel }}
                </span>
              </li>
              <li>
                <router-link :to="{ name: 'Profile' }" class="dropdown-item py-2 text-white font-weight-bold">
                  <i class="mdi mdi-shield-account mr-2 text-info" aria-hidden="true"></i> {{ $t('nav.profileSettings') }}
                </router-link>
              </li>
              <li v-if="isOwner">
                <router-link to="/billing" class="dropdown-item py-2 text-white font-weight-bold">
                  <i class="mdi mdi-treasure-chest mr-2 text-warning" aria-hidden="true"></i> {{ $t('nav.companyBilling') }}
                </router-link>
              </li>
              <li><hr class="dropdown-divider my-1" style="border-color: rgba(56, 189, 248, 0.25);"></li>
              <li>
                <router-link :to="{ name: 'Logout' }" class="dropdown-item py-2 text-danger font-weight-bold">
                  <i class="mdi mdi-logout mr-2" aria-hidden="true"></i> {{ $t('nav.logOut') }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>

    <!-- Command Palette Component -->
    <CommandPalette
      :isOpen="showCmd"
      :isSuperAdmin="isSuperAdmin"
      :isMaker="isMaker"
      :isOwner="isOwner"
      @close="showCmd = false"
    />
  </div>
</template>

<script>
import config from '@/config/config';
import CommandPalette from '@/components/CommandPalette.vue';

export default {
  name: 'NavbarComponents',
  components: {
    CommandPalette
  },
  data() {
    return {
      config,
      showCmd: false,
      isOffcanvasActive: false
    };
  },
  computed: {
    user() {
      try {
        const raw = this.$store.state.user || localStorage.getItem("user");
        return raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null;
      } catch (e) {
        return null;
      }
    },
    userName() {
      return this.user?.data?.name || this.user?.name || this.$t('nav.defaultUserName');
    },
    userEmail() {
      return this.user?.data?.email || this.user?.email || '';
    },
    userRole() {
      return this.user?.data?.role || this.user?.role || '';
    },
    userRolesList() {
      return (this.userRole || '').split(',').map(r => r.trim().toLowerCase()).filter(Boolean);
    },
    isSuperAdmin() {
      return this.userRolesList.includes('superadmin');
    },
    isMaker() {
      return this.userRolesList.includes('maker') && !this.userRolesList.includes('owner') && !this.isSuperAdmin;
    },
    isOwner() {
      return this.userRolesList.includes('owner') || (!this.isSuperAdmin && !this.isMaker);
    },
    roleBadgeLabel() {
      if (this.isSuperAdmin) return this.$t('nav.platformOperator');
      if (this.userRolesList.includes('owner')) return this.$t('nav.roleOwner');
      if (this.userRolesList.includes('maker')) return this.$t('nav.roleMaker');
      return this.$t('nav.enterpriseMember');
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        this.showCmd = !this.showCmd;
      }
    },
    toggleSidebarMinimize() {
      document.body.classList.toggle('sidebar-icon-only');
    },
    toggleOffcanvas() {
      const sidebar = document.querySelector('.sidebar-offcanvas');
      if (sidebar) {
        sidebar.classList.toggle('active');
        this.isOffcanvasActive = sidebar.classList.contains('active');
      }
    },
    closeOffcanvas() {
      const sidebar = document.querySelector('.sidebar-offcanvas');
      if (sidebar) {
        sidebar.classList.remove('active');
      }
      this.isOffcanvasActive = false;
    }
  }
};
</script>

<style scoped>
.kh-hud-btn {
  width: 36px;
  height: 36px;
  border-radius: 7px;
  background: #040d21;
  border: 1.5px solid #2b6cb0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.kh-cmd-trigger {
  background: #040d21;
  border: 2px solid #25538f;
  border-radius: 8px;
  height: 36px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.kh-cmd-trigger:hover {
  border-color: #38bdf8;
}

.kh-cmd-kbd {
  font-family: 'Russo One', sans-serif;
  font-size: 0.68rem;
  background: linear-gradient(180deg, #ffe566 0%, #f59e0b 100%);
  color: #1a0f00;
  padding: 2px 6px;
  border-radius: 4px;
}

.kh-realm-pill {
  background: #040d21;
  border: 1.5px solid #f59e0b;
  border-radius: 7px;
}

.kh-lang-switch {
  background: #040d21;
  border: 1.5px solid #25538f;
  border-radius: 7px;
  gap: 2px;
}

.kh-lang-opt {
  background: transparent;
  border: none;
  color: #7dd3fc;
  font-family: 'Russo One', sans-serif;
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 5px;
  cursor: pointer;
}
.kh-lang-opt.active {
  background: linear-gradient(180deg, #38bdf8 0%, #0284c7 100%);
  color: #ffffff;
}

.kh-user-pill {
  background: #040d21;
  border: 2px solid #f59e0b;
  border-radius: 8px;
  cursor: pointer;
}

.kh-user-crest {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  background: linear-gradient(180deg, #ffe566 0%, #d97706 100%);
  color: #1a0f00;
  font-family: 'Russo One', sans-serif;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kh-dropdown-menu {
  background: linear-gradient(180deg, #0b2247 0%, #071936 100%) !important;
  border: 2px solid #38bdf8 !important;
  border-radius: 10px !important;
}
.kh-dropdown-menu .dropdown-item:hover {
  background: rgba(56, 189, 248, 0.18) !important;
}
</style>
